'use client';
import React, { useState } from 'react';
import Link from 'next/link';

type FormState = 'idle' | 'submitting' | 'success';

export default function QuoteForm() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('submitting');
    // Redirect to WhatsApp
    const waNumber = '6281253837777';
    const waText = `Halo Agro Teknologi Nusantara!%0A%0ANama: ${encodeURIComponent(form.name)}%0AEmail: ${encodeURIComponent(form.email)}%0ATelp: ${encodeURIComponent(form.phone)}%0ASubjek: ${encodeURIComponent(form.subject)}%0A%0APesan:%0A${encodeURIComponent(form.message)}`;
    window.open(`https://wa.me/${waNumber}?text=${waText}`, '_blank');
    setTimeout(() => setFormState('success'), 800);
  };

  const subjectOptions = [
    { value: 'layanan-iot', label: 'Layanan IoT Pertanian' },
    { value: 'smart-farming', label: 'Smart Farming & Otomatisasi' },
    { value: 'pelatihan', label: 'Pelatihan & Edukasi' },
    { value: 'konsultasi', label: 'Jasa Konsultasi' },
    { value: 'komunitas', label: 'Komunitas & LMS' },
    { value: 'kolaborasi', label: 'Kolaborasi & Kemitraan' },
    { value: 'lainnya', label: 'Lainnya' },
  ];

  if (formState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 blueprint-grid">
        <div className="max-w-lg w-full text-center">
          <div className="border border-chartreuse/30 rounded-sm p-12 bg-pond/40">
            <div className="font-mono font-black text-chartreuse text-5xl mb-6">✓</div>
            <h2 className="font-mono font-bold text-2xl text-text-primary mb-4">
              Pesan Terkirim
            </h2>
            <p className="text-text-muted leading-relaxed mb-4">
              Terima kasih! Tim kami akan segera menghubungi Anda.
            </p>
            <p className="text-text-muted text-sm mb-8 font-mono">
              Pesan juga telah terbuka di WhatsApp.
            </p>
            <Link
              href="/"
              className="btn-outline-chartreuse px-8 py-3 rounded-sm text-xs uppercase tracking-widest inline-block"
            >
              ← Kembali ke Beranda
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 px-6 md:px-10 blueprint-grid">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-px bg-chartreuse opacity-60" />
            <span className="annotation">HUBUNGI KAMI</span>
          </div>
          <h1 className="font-mono font-bold text-4xl md:text-5xl text-text-primary mb-4 leading-tight">
            Hubungi<br />
            <span className="text-chartreuse">Tim Kami</span>
          </h1>
          <p className="text-text-muted text-lg leading-relaxed">
            Kalau ada pertanyaan yang belum terjawab, tim kami siap bantu secara langsung.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-8">
          {/* Personal details */}
          <div>
            <label className="annotation block mb-4">01 / DATA DIRI</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { name: 'name', label: 'Nama Lengkap', placeholder: 'Masukkan nama lengkap Anda', required: true },
                { name: 'email', label: 'Email', placeholder: 'email@contoh.com', type: 'email', required: true },
                { name: 'phone', label: 'Nomor Telepon / WhatsApp', placeholder: '+62 812 5383 7777', required: true },
                { name: 'subject', label: 'Subjek', type: 'select', required: true },
              ].map((field) => (
                <div key={field.name} className={field.name === 'name' ? 'md:col-span-2' : ''}>
                  <label className="annotation block mb-2">{field.label}</label>
                  {field.type === 'select' ? (
                    <select
                      name={field.name}
                      value={form[field.name as keyof typeof form]}
                      onChange={handleChange}
                      required={field.required}
                      className="w-full bg-canopy/60 border border-chartreuse/20 rounded-sm px-5 py-3.5 font-mono text-sm text-text-primary focus:outline-none focus:border-chartreuse/50 transition-colors appearance-none"
                    >
                      <option value="" className="bg-canopy">Pilih subjek</option>
                      {subjectOptions.map((opt) => (
                        <option key={opt.value} value={opt.label} className="bg-canopy">
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type || 'text'}
                      name={field.name}
                      placeholder={field.placeholder}
                      value={form[field.name as keyof typeof form]}
                      onChange={handleChange}
                      required={field.required}
                      className="w-full bg-canopy/60 border border-chartreuse/20 rounded-sm px-5 py-3.5 font-mono text-sm text-text-primary placeholder-text-muted/30 focus:outline-none focus:border-chartreuse/50 transition-colors"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="annotation block mb-4">02 / PESAN ANDA</label>
            <textarea
              name="message"
              placeholder="Ceritakan kebutuhan Anda — layanan apa yang dibutuhkan, skala pertanian, lokasi, dan informasi lain yang relevan..."
              value={form.message}
              onChange={handleChange}
              required
              rows={5}
              className="w-full bg-canopy/60 border border-chartreuse/20 rounded-sm px-5 py-3.5 font-mono text-sm text-text-primary placeholder-text-muted/30 focus:outline-none focus:border-chartreuse/50 transition-colors resize-none"
            />
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={formState === 'submitting'}
              className="btn-chartreuse px-10 py-4 rounded-sm text-sm uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {formState === 'submitting' ? 'Membuka WhatsApp...' : 'Hubungi via WhatsApp →'}
            </button>
            <p className="font-mono text-xs text-text-muted/50 tracking-wider">
              Tim kami merespons dalam 1x24 jam kerja
            </p>
          </div>
        </form>

        {/* WhatsApp direct button */}
        <div className="mt-8 border border-chartreuse/10 rounded-sm p-6 bg-pond/20">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-green-600/20 flex items-center justify-center shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-green-500">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div>
              <div className="font-mono text-sm text-text-primary font-bold mb-1">
                Hubungi langsung via WhatsApp
              </div>
              <a
                href="https://wa.me/6281253837777?text=Halo%20Agro Teknologi Nusantara,%20saya%20ingin%20bertanya%20tentang%20layanan"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs text-chartreuse hover:underline"
              >
                +62 812 5383 7777
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
